import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import caseApi from '../../shared/api/cases/case';
import { decapitalize } from '@/shared/utils/helpers';
import signalRMessages from '@/redux/actions/signalRMessages';
import useDebouncedHandler from '../../shared/hooks/useDebouncedHandler';
import useNotification from '../../shared/hooks/UseNotification';
import useSocketEvent from '../../shared/hooks/useSocketEvent';
import { SELECTOR_CASES_QUERY_KEY, SELECTOR_CASES_STALE_TIME } from './selectorCasesQuery';

const SOCKET_INVALIDATION_DEBOUNCE = 2000;

const useGetSelectorCases = ({ inactive, select = (data) => data }) => {
  const { sendDefaultError } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const invalidateOnRemoteChange = useDebouncedHandler(
    () => queryClient.invalidateQueries(SELECTOR_CASES_QUERY_KEY),
    SOCKET_INVALIDATION_DEBOUNCE,
  );
  useSocketEvent(
    [
      signalRMessages.CaseUpdated,
      signalRMessages.CaseRelationAdded,
      signalRMessages.CaseRelationUpdated,
      signalRMessages.CaseRelationRemoved,
    ],
    invalidateOnRemoteChange,
  );

  const { data, isLoading } = useQuery({
    enabled: !inactive,
    onError: (error) => {
      sendDefaultError(error);
    },
    queryFn: async () => {
      const result = await caseApi.getSelectorCases();

      const formatted = result.map((c) => ({
        label: `${c.reference}${
          c.description && c.description !== '-'
            ? ` | ${c.description.length > 50 ? `${c.description.slice(0, 50)}...` : c.description}`
            : ''
        }${c.requestors && c.requestors.length ? ` | ${c.requestors.join(', ')}` : ''} | ${t(
          decapitalize(c.status),
        )}`,
        type: c.caseType,
        value: c.id,
      }));

      return { formatted, result };
    },
    queryKey: SELECTOR_CASES_QUERY_KEY,
    refetchOnWindowFocus: false,
    select,
    staleTime: SELECTOR_CASES_STALE_TIME,
  });

  return {
    selectorCases: data?.result || [],
    selectorCasesFormatted: data?.formatted || [],
    selectorCasesIsLoading: isLoading,
  };
};

export default useGetSelectorCases;
